// features/cx/disconnect/api.ts

import {
  MeterReadingsFilters,
  TimeSlotChoice,
  UtilityConfigFilters,
  UtilityRequestConfiguration,
} from "@features/cx/shared/types/consumerRequest";
import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  bxApiClient,
  communicationApiClient,
  cxApiClient,
  mxApiClient,
} from "@shared/api/clients/apiClientFactory";
import { ApiResponse, DropdownItem } from "@shared/api/types";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import {
  ActivityLogFilters,
  ActivityLogsResponse,
  ConsumerBillData,
  ConsumerBillFilters,
  ConsumerMetaData,
  ConsumerSearchFilters,
  ConsumerSearchResult,
  ConsumerSupportDisconnectPayload,
  CreateBillGenerationPayload,
  CreateDisconnectionRequestPayload,
  DisconnectionFilters,
  DisconnectionRequest,
  DisconnectionSummary,
  SingleBillApprovePayload,
  UpdateBillGenerationPayload,
  UpdateDisconnectionRequestPayload,
} from "./types";
import { normalizeDate } from "@shared/utils/dateUtils";

export interface RefundSettlementPayload {
  disposition: string;
  refund_mode?: string;
  credit_note_note?: string;
  security_deposit_amount: string;
  credit_amount: string;
  total_refund_amount: string;
  [key: string]: any;
}

// API Functions
export const disconnectionApi = {
  // Consumer Search
  searchConsumer: async (
    filters: ConsumerSearchFilters,
  ): Promise<{ result: ConsumerSearchResult[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "consumer-search",
      ["info"],
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        query.push("search_data", filters.searchData);
        query.push("request_type", filters.requestType);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ConsumerSearchResult[] }>(
      response.data,
    );
  },

  // Consumer Meta Data
  getConsumerMetaData: async (params: {
    consumerId: number;
    remoteUtilityId: number;
  }): Promise<{ result: ConsumerMetaData }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-meta-data",
      (query) => {
        query.push("consumer_id", params.consumerId.toString());
        query.push("remote_utility_id", getRemoteUtilityId());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  getUtilityRequestConfiguration: async (
    filters: UtilityConfigFilters,
  ): Promise<{ result: UtilityRequestConfiguration[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "utility-request-configuration",
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        query.push("request_type", filters.requestType);
        if (filters.disablePagination) query.push("disable_pagination", "true");
        if (filters.searchData) query.push("search_data", filters.searchData);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: UtilityRequestConfiguration[] }>(
      response.data,
    );
  },

  // Consumer Bill Data
  getConsumerBill: async (
    filters: ConsumerBillFilters,
  ): Promise<{ result: ConsumerBillData }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-bill",
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        query.push("consumer_id", filters.consumerId.toString());
        query.push("fetch_last_six_records", "True");
        query.push("bill_data", "True");
        return query;
      },
    );

    const response = await cxApiClient.get<{ result: ConsumerBillData }>(url);
    return transformJsonToObject<{ result: ConsumerBillData }>(response.data);
  },

  // Disconnection Request List
  getDisconnectionRequests: async (
    filters: DisconnectionFilters,
  ): Promise<ApiResponse<DisconnectionRequest>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "connection-status-request-v2",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", getRemoteUtilityId());
        if (filters.requestType)
          query.push("request_type", filters.requestType);
        if (filters.activeTab) query.push("active_tab", filters.activeTab);
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.status) query.push("status", filters.status);
        if (filters.name) query.push("name", filters.name);
        return query;
      },
    );

    const response =
      await cxApiClient.get<ApiResponse<DisconnectionRequest>>(url);
    return transformJsonToObject<ApiResponse<DisconnectionRequest>>(
      response.data,
    );
  },

  // Add this method to your disconnection API object

  exportDisconnectionRequests: async (
    filters: DisconnectionFilters,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "connection-status-request",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", getRemoteUtilityId());
        if (filters.requestType)
          query.push("request_type", filters.requestType);
        if (filters.activeTab) query.push("active_tab", filters.activeTab);
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.status) query.push("status", filters.status);
        if (filters.name) query.push("name", filters.name);
        query.push("export_format", "csv");
        return query;
      },
    );

    const response = await cxApiClient.get(url);

    return response.data;
  },

  // Create Disconnection Request
  createDisconnectionRequest: async (
    payload: CreateDisconnectionRequestPayload,
  ): Promise<DisconnectionRequest> => {
    const url = ApiEndpoints.createUrl("cx", "connection-status-request");
    const response = await cxApiClient.post(url, payload);
    return transformJsonToObject<DisconnectionRequest>(response.data);
  },

  // Get Disconnection Request Detail
  getDisconnectionRequestDetail: async (
    requestId: number,
    remoteUtilityId: string,
  ): Promise<DisconnectionRequest> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      `connection-request-detail`,
      [requestId.toString()],
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<DisconnectionRequest>(response.data);
  },

  // Update Disconnection Request
  updateDisconnectionRequest: async (
    requestId: number,
    payload: UpdateDisconnectionRequestPayload,
  ): Promise<DisconnectionRequest> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "connection-status-request",
      [requestId.toString()],
    );
    const response = await cxApiClient.put(url, payload);
    return transformJsonToObject<DisconnectionRequest>(response.data);
  },

  // Delete Disconnection Request
  deleteDisconnectionRequest: async (requestId: number): Promise<void> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "connection-status-request",
      [requestId.toString()],
    );
    await cxApiClient.delete(url);
  },

  // Get Disconnection Summary
  getDisconnectionSummary: async (params: {
    remoteUtilityId: number;
    requestType: string;
  }): Promise<DisconnectionSummary> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "connection-status-request-summary",
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        query.push("request_type", params.requestType);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<DisconnectionSummary>(response.data.result);
  },

  // Bill Generation APIs
  createBillGeneration: async (
    payload: CreateBillGenerationPayload,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrl("bx", "single-bill-generation");
    const response = await bxApiClient.post(url, payload);
    return response.data;
  },

  updateBillGeneration: async (
    billId: number,
    payload: UpdateBillGenerationPayload,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "bx",
      "single-bill-generation",
      [billId.toString()],
    );
    const response = await bxApiClient.patch(url, payload);
    return response.data;
  },

  approveSingleBill: async (
    billId: number,
    payload: SingleBillApprovePayload,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "bx",
      "single-bill-approve",
      [billId.toString()],
    );
    const response = await bxApiClient.put(url, payload);
    return response.data;
  },

  // Consumer Support Disconnect
  disconnectConsumerSupport: async (
    consumerId: number,
    payload: ConsumerSupportDisconnectPayload,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "consumer-support",
      ["disconnect", consumerId.toString()],
    );
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },

  getStatus: async (params: {
    remoteUtilityId: number;
    tab: number;
  }): Promise<{ result: DropdownItem[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "choices",
      ["consumer_support_request_status"],
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        query.push("tab", params.tab);
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: DropdownItem[] }>(response.data);
  },

  getCategory: async (params: {
    remoteUtilityId: number;
  }): Promise<{ result: DropdownItem[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "utility-request-configuration",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId);
        query.push("request_type", "Disconnect Permanent");
        query.push("disable_pagination", true);
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response?.data.result.map((item) => ({
      label: item.name,
      value: item.name,
    }));
  },

  // Activity Logs
  getActivityLogs: async (
    filters: ActivityLogFilters,
  ): Promise<ActivityLogsResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "activity-log",
      (query) => {
        query.push("remote_utility_id", filters.remoteUtilityId.toString());
        query.push("request_id", filters.requestId.toString());
        query.push("module", filters.module);
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        return query;
      },
    );

    const response = await communicationApiClient.get(url);
    return transformJsonToObject<ActivityLogsResponse>(response.data);
  },

  getTimeSlotChoices: async (): Promise<{ result: TimeSlotChoice[] }> => {
    const url = ApiEndpoints.createUrl("cx", "choices/time_slot");
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: TimeSlotChoice[] }>(response.data);
  },

  getOwnerTypeChoices: async (): Promise<{ result: DropdownItem[] }> => {
    const url = ApiEndpoints.createUrl("cx", "choices/owner_type");
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: DropdownItem[] }>(response.data);
  },

  getBillTemplates: async (remoteUtilityId: number): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "get-bill-template",
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        return query;
      },
    );
    const response = await cxApiClient.get(url);

    return response.data.result;
  },

  getMeterReadings: async (filters: MeterReadingsFilters): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "meter-reading-details",
      (query) => {
        query.push("remote_utility_id", filters.remoteUtilityId.toString());
        // Add each meter_id separately
        filters.meterId.forEach((id) => {
          query.push("meter_id", id.toString());
        });
        query.push("invoice_start_date", normalizeDate(filters.startDate));
        query.push("invoice_end_date", normalizeDate(filters.endDate));

        // query.push("invoice_start_date", filters.startDate);
        // query.push("invoice_end_date", filters.endDate);
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data.result;
  },

  getConsumerCsvTemplate: async () => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "bulk-requests/consumers/csv-template",
    );
    const response = await cxApiClient.get(url);
    return response.data.result ?? response.data;
  },

  validateConsumerCsv: async (file: File, remoteUtilityId: number) => {
    const url = ApiEndpoints.createUrl(
      "cx",
      "bulk-requests/consumers/validate-csv",
    );
    const formData = new FormData();
    formData.append("file", file);
    formData.append("remote_utility_id", remoteUtilityId.toString());
    const response = await cxApiClient.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data.result ?? response.data;
  },

  searchConsumersInfo: async (params: {
    remoteUtilityId: number;
    requestType: string;
    page: number;
    limit: number;
    searchData?: string;
    category?: string;
    subCategory?: string;
    premise?: string;
    status?: string;
    accountType?: string;
    is_outstanding_info?: number;
  }) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-search/info",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId);
        if (params.searchData) {
          query.push("search_data", params.searchData);
        }
        query.push("request_type", params.requestType);
        query.push("show_address_info", 1);
        if (params.is_outstanding_info !== undefined) {
          query.push("is_outstanding_info", params.is_outstanding_info);
        }
        query.push("page", params.page);
        query.push("limit", params.limit);
        if (params.category) query.push("category", params.category);
        if (params.subCategory) query.push("sub_category", params.subCategory);
        if (params.premise) query.push("premise", params.premise);
        if (params.status) query.push("status", params.status);
        if (params.accountType) query.push("account_type", params.accountType);
        query.push("is_pagination", "1");
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },
  bulkDisconnectDetails: async (batchId: string) => {
    const url = ApiEndpoints.createUrl("cx", `bulk-requests/${batchId}`);
    const response = await cxApiClient.get(url);
    return response.data.result ?? response.data;
  },

  createBulkRequest: async (payload: any) => {
    const url = ApiEndpoints.createUrl("cx", "bulk-requests");
    const response = await cxApiClient.post(url, payload);
    return response.data.result ?? response.data;
  },

  acknowledgeBulkRequest: async (batchId: string) => {
    const url = ApiEndpoints.createUrl(
      "cx",
      `bulk-requests/${batchId}/acknowledge`,
    );
    const response = await cxApiClient.post(url);
    return response.data.result ?? response.data;
  },

  approveBulkRequest: async (batchId: string) => {
    const url = ApiEndpoints.createUrl(
      "cx",
      `bulk-requests/${batchId}/approve`,
    );
    const response = await cxApiClient.post(url);
    return response.data.result ?? response.data;
  },

  rejectBulkRequest: async (
    batchId: string,
    payload: { rejection_reason: string },
  ) => {
    const url = ApiEndpoints.createUrl("cx", `bulk-requests/${batchId}/reject`);
    const response = await cxApiClient.post(url, payload);
    return response.data.result ?? response.data;
  },

  holdBulkRequest: async (batchId: string) => {
    const url = ApiEndpoints.createUrl("cx", `bulk-requests/${batchId}/hold`);
    const response = await cxApiClient.post(url);
    return response.data.result ?? response.data;
  },

  resumeBulkRequest: async (batchId: string) => {
    const url = ApiEndpoints.createUrl("cx", `bulk-requests/${batchId}/resume`);
    const response = await cxApiClient.post(url);
    return response.data.result ?? response.data;
  },

  closeBulkRequest: async (batchId: string, payload?: any) => {
    const url = ApiEndpoints.createUrl("cx", `bulk-requests/${batchId}/closed`);
    const response = await cxApiClient.post(url, payload);
    return response.data.result ?? response.data;
  },

  getBulkRequestConsumersList: async (params: {
    batchId: string;
    remoteUtilityId: number;
    page: number;
    limit: number;
    searchData?: string;
    category?: string;
    subCategory?: string;
    premise?: string;
  }) => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "bulk-requests/consumers-list",
      [params.batchId],
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId);
        query.push("page", params.page);
        query.push("limit", params.limit);
        if (params.searchData) {
          query.push("search_data", params.searchData);
        }
        if (params.category) {
          query.push("category", params.category);
        }
        if (params.subCategory) {
          query.push("sub_category", params.subCategory);
        }
        if (params.premise) {
          query.push("premise", params.premise);
        }
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data.result ?? response.data;
  },

  downloadMeterCSV: async (batchId: string) => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "bulk-requests/meter-csv/download",
      [batchId],
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        return query;
      },
    );
    const response = await cxApiClient.get(url, { responseType: "blob" });
    return response.data;
  },

  getBulkRequestMeterList: async (params: {
    batchId: string;
    remoteUtilityId: number;
    page: number;
    limit: number;
    searchData?: string;
    category?: string;
    subCategory?: string;
    accountType?: string;
  }) => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "bulk-requests/meter-list",
      [params.batchId],
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId);
        query.push("batch_id", params.batchId);
        query.push("page", params.page);
        query.push("limit", params.limit);
        if (params.searchData) {
          query.push("search_data", params.searchData);
        }
        if (params.category) {
          query.push("category", params.category);
        }
        if (params.subCategory) {
          query.push("sub_category", params.subCategory);
        }
        if (params.accountType) {
          query.push("account_type", params.accountType);
        }
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  uploadMeterCSV: async (batchId: string, file: File) => {
    const url = ApiEndpoints.createUrl("mx", "bulk-requests/meter-csv/upload");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("batch_id", batchId);
    formData.append("remote_utility_id", getRemoteUtilityId().toString());

    const response = await mxApiClient.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data.result ?? response.data;
  },

  generateBulkBills: async (batchId: string, payload: any) => {
    const url = ApiEndpoints.createUrl(
      "cx",
      `bulk-requests/generate-bills/${batchId}`,
    );
    const response = await cxApiClient.post(url, payload);
    return response.data.result ?? response.data;
  },

  trackBillsProgress: async (taskId: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "bx",
      "track-bills-progress",
      (query) => {
        query.push("task_id", taskId);
        return query;
      },
    );
    const response = await bxApiClient.get(url);
    return response.data.result ?? response.data;
  },

  retryBills: async (runId: number, payload: any) => {
    const url = ApiEndpoints.createUrl("bx", `retry-bills/${runId}`);
    const response = await bxApiClient.put(url, payload);
    return response.data.result ?? response.data;
  },

  getBillConfigMeta: async (remoteUtilityId: number) => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "bx",
      "bill-config-meta",
      [],
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        return query;
      },
    );
    const response = await bxApiClient.get(url);
    return response.data.result ?? response.data;
  },

  getGeneratedBills: async (filters: {
    run_id: string | number;
    remote_utility_id: number;
    page?: number;
    limit?: number;
  }) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "bx",
      "billing/bills",
      (query) => {
        query.push("run_id", filters.run_id.toString());
        query.push("remote_utility_id", filters.remote_utility_id.toString());
        query.push("page", (filters.page || 1).toString());
        query.push("limit", (filters.limit || 50).toString());
        return query;
      },
    );
    const response = await bxApiClient.get(url);
    return response.data;
  },

  approveBulkBill: async (runId: string | number) => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "bx",
      "create-bulk-bill/approve",
      [runId.toString()],
    );
    const payload = {
      is_email: true,
      consents: [
        "consumer_data_accuracy",
        "meter_reading_validation",
        "tariff_rate_application",
        "bill_calculation_accuracy",
        "adjustments_credits",
        "bill_distribution_authorization",
        "payment_processing_authorization",
        "consumer_communication_authorization",
        "final_billing_cycle_approval",
      ],
    };
    const response = await bxApiClient.put(url, payload);
    return response.data;
  },

  downloadBulkRequestConsumersList: async (batchId: string) => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "bulk-requests/consumers-list-download",
      [batchId],
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        return query;
      },
    );
    const response = await cxApiClient.get(url, { responseType: "blob" });
    return response.data;
  },

  saveSingleMeterReading: async (payload: any) => {
    const url = ApiEndpoints.createUrl("mx", "single-meter-reading/save");
    const response = await mxApiClient.post(url, payload);
    return response.data.result ?? response.data;
  },

  downloadAllBulkRequestBills: async (batchId: string) => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "bx",
      "download-all-bill",
      [batchId],
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        return query;
      },
    );
    const response = await bxApiClient.get(url, { responseType: "blob" });
    return response.data;
  },

  getBillErrorsCsv: async (taskId: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "bx",
      "bills/errors-csv",
      (query) => {
        query.push("task_id", taskId);
        return query;
      },
    );
    const response = await bxApiClient.get(url, { responseType: "blob" });
    return response.data;
  },
};

export const submitConsumerSelection = async (payload: any) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `single-bill-generation`,
  );
  const response = await bxApiClient.post(url, payload);
  return response.data?.result;
};

export const downloadBillTemplate = async (billId: string): Promise<Blob> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `render-dynamic-template/${billId}`,
  );
  const response = await bxApiClient.get(url, {
    responseType: "blob",
  });
  return response.data;
};

export const approveBill = async (billId: any, billDraftId: number) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `single-bill-approve/${billId}`,
    (query) => {
      query.push("bill_draft_id", billDraftId);
    },
  );
  const response = await bxApiClient.put(url, {
    send_communication: true,
  });
  return response.data?.result;
};

export const approveDiscoonnectionBill = async (
  billId: any,
  requestID: number,
) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    `single-bill-approve/${billId}/${requestID}`,
    (query) => {
      query.push("remote_utility_id", REMOTE_UTILITY_ID);
    },
  );

  const response = await cxApiClient.put(url);
  return response.data?.result;
};

export const rejectBill = async (billId: any) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bill/${billId}/REJECTED`,
  );
  const response = await bxApiClient.put(url);
  return response.data?.result;
};

export const updateDisconnection = async (
  requestId: number,
  payload: UpdateDisconnectionRequestPayload,
): Promise<DisconnectionRequest> => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    "cx",
    "disconnect-consumer-request",
    [requestId.toString()],
  );
  const response = await cxApiClient.put(url, payload);
  return transformJsonToObject<DisconnectionRequest>(response.data);
};

export const getWorkOrderDetails = async (search: string): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "search-job-card",
    (query) => {
      query.push("remote_utility_id", getRemoteUtilityId());
      query.push("search_data", search);

      return query;
    },
  );
  const response = await cxApiClient.get(url);
  return response.data.result;
};

export const getBillSummary = async (batchId: string): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    `bulk-requests/bills-summary/${batchId}`,
    (query) => {
      query.push("remote_utility_id", getRemoteUtilityId());
      return query;
    },
  );
  const response = await cxApiClient.get(url);
  return response.data.result;
};

export const getMeterInfo = async (
  deviceNo: string,
  utilityService: string,
  premise: string,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "search-meter",
    (query) => {
      query.push("remote_utility_id", getRemoteUtilityId());
      query.push("premise", premise);
      if (deviceNo) {
        query.push("device_number", deviceNo);
      }
      query.push("utility_service", utilityService);
      query.push("page", 1);
      query.push("limit", 5);

      return query;
    },
  );
  const response = await cxApiClient.get(url);
  return response.data.result;
};

export const getMeterHistory = async (consumerId: number): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "meter-history",
    (query) => {
      query.push("remote_utility_id", getRemoteUtilityId());
      query.push("consumer_id", consumerId);
      return query;
    },
  );
  const response = await cxApiClient.get(url);
  return response.data.result;
};

export const refundRemainingAmount = async (payload) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    `disconnect-permanent-refund`,
  );

  const response = await cxApiClient.post(url, payload);
  return response.data?.result;
};

export const postMasterAccountAction = async (
  requestId: number,
  payload: {
    master_account_action: "make_individual" | "move";
    new_master_account_id?: number;
  },
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    "cx",
    "master-account-action",
    [requestId.toString()],
  );
  const response = await cxApiClient.patch(url, payload);
  return response.data?.result ?? response.data;
};

export const refundSettlement = async (payload: any) => {
  const { requestId, ...body } = payload;
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    `payments/disconnection/${requestId}/refund-settlement`,
  );
  const response = await cxApiClient.post(url, body);
  return response.data?.result || response.data;
};
